import React, { useState } from 'react';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Tooltip } from '@mui/material';

import { OperationExecutionTypeModel, OperationExecutionTypesModel, OperationExposureModel, SchemaSummaryModel } from '../../../api/api';
import OperationStepper, { OperationFormData, isOperationWizardStepValid, operationWizardStepCount } from './OperationStepper';

interface Props {
	onCreate: (name: string, description: string, executionType: OperationExecutionTypesModel, exposure: OperationExposureModel, selectedEntityIds: string[]) => Promise<string | void>;
	executionTypes?: OperationExecutionTypeModel[];
	availableEntities: SchemaSummaryModel[];
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

const initialFormData: OperationFormData = {
	name: '',
	description: '',
	exposure: OperationExposureModel.Api,
	selectedTypeIds: [],
	creationMode: 'blank',
	selectedEntityIds: [],
};

const CreateOperationDialog: React.FC<Props> = ({ onCreate, executionTypes, availableEntities, open, onOpenChange }) => {
	const [activeStep, setActiveStep] = useState(0);
	const [formData, setFormData] = useState<OperationFormData>(initialFormData);
	const [isSaving, setIsSaving] = useState(false);

	const isLastStep = activeStep === operationWizardStepCount - 1;
	const canContinue = isOperationWizardStepValid(formData, activeStep);

	const handleClose = () => {
		onOpenChange(false);
		setActiveStep(0);
		setFormData(initialFormData);
		setIsSaving(false);
	};

	const handleBack = () => {
		setActiveStep((currentStep) => Math.max(currentStep - 1, 0));
	};

	const handleNext = () => {
		if (!canContinue) {
			return;
		}

		setActiveStep((currentStep) => Math.min(currentStep + 1, operationWizardStepCount - 1));
	};

	const handleSave = async () => {
		if (!isLastStep || !canContinue || formData.selectedTypeIds.length === 0) {
			return;
		}

		setIsSaving(true);

		try {
			await onCreate(formData.name, formData.description, formData.selectedTypeIds[0], formData.exposure, formData.selectedEntityIds);
			handleClose();
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			fullWidth
			maxWidth={false}
			slotProps={{
				paper: {
					sx: {
						width: '50vw',
						maxWidth: 'none',
						minHeight: '50vh',
						display: 'flex',
						flexDirection: 'column',
					},
				},
			}}
		>
			<DialogTitle>
				<Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
					<Stack direction="row" alignItems="center" spacing={1}>
						<AddCircleOutlineIcon color="primary" />
						<span>Create New Operation</span>
					</Stack>
					<Tooltip title="Close">
						<IconButton aria-label="Close create operation dialog" edge="end" onClick={handleClose} size="small">
							<CloseIcon />
						</IconButton>
					</Tooltip>
				</Stack>
			</DialogTitle>
			<DialogContent sx={{ display: 'flex', flex: 1 }}>
				<OperationStepper
					activeStep={activeStep}
					availableTargetEnitities={availableEntities}
					formData={formData}
					onFormDataChange={setFormData}
					executionTypes={executionTypes ?? []}
				/>
			</DialogContent>
			<DialogActions sx={{ justifyContent: 'space-between' }}>
				<Button onClick={handleBack} disabled={activeStep === 0 || isSaving} startIcon={<ArrowBackIcon />}>
					Back
				</Button>
				<Box>
					{isLastStep ? (
						<Button onClick={handleSave} variant="contained" disabled={!canContinue || isSaving}>
							{isSaving ? 'Creating...' : 'Create'}
						</Button>
					) : (
						<Button onClick={handleNext} variant="contained" disabled={!canContinue || isSaving} endIcon={<NavigateNextIcon />}>
							Next
						</Button>
					)}
				</Box>
			</DialogActions>
		</Dialog>
	);
};

export default CreateOperationDialog;
